import { mode } from '@chakra-ui/theme-tools';

const modernBg = '#08111f';
const modernShellBg = '#0b1224';
const modernSurface = 'rgba(15, 23, 42, 0.82)';
const modernSurfaceStrong = 'rgba(17, 24, 39, 0.92)';
const modernSurfaceSoft = 'rgba(255, 255, 255, 0.055)';
const modernBorder = 'rgba(148, 163, 184, 0.18)';
const modernBorderStrong = 'rgba(148, 163, 184, 0.28)';
const modernText = '#f8fafc';
const modernMuted = '#cbd5e1';
const modernSubtle = '#94a3b8';
const modernAccent = '#4f46e5';

export const globalStyles = {
	colors: {
		brand: {
			50: '#EFEBFF',
			100: '#E9E3FF',
			200: '#422AFB',
			300: '#422AFB',
			400: '#7551FF',
			500: '#422AFB',
			600: '#3311DB',
			700: '#02044A',
			800: '#190793',
			900: '#11047A'
		},
		brandScheme: {
			50: '#EFEBFF',
			100: '#E9E3FF',
			200: '#7551FF',
			300: '#7551FF',
			400: '#7551FF',
			500: '#422AFB',
			600: '#3311DB',
			700: '#02044A',
			800: '#190793',
			900: '#02044A'
		},
		brandTabs: {
			50: '#EFEBFF',
			100: '#E9E3FF',
			200: '#422AFB',
			300: '#422AFB',
			400: '#422AFB',
			500: '#422AFB',
			600: '#3311DB',
			700: '#02044A',
			800: '#190793',
			900: '#02044A'
		},
        horizonGreen: {
          50: '#E1FFF4',
          100: '#BDFFE7',
          200: '#7BFECE',
          300: '#39FEB6',
          400: '#01F99E',
          500: '#01B574',
          600: '#01935D',
          700: '#016B44',
          800: '#00472D',
          900: '#002417',
        },
        horizonOrange: {
          50: '#FFF7EB',
          100: '#FFF1DB',
          200: '#FFE2B8',
          300: '#FFD28F',
          400: '#FFC46B',
          500: '#FFB547',
          600: '#FF9B05',
          700: '#C27400',
          800: '#855000',
          900: '#422800',
          950: '#1F1200',
        },
        horizonRed: {
          50: '#FCE8E8',
          100: '#FAD1D1',
          200: '#F4A4A4',
          300: '#EF7676',
          400: '#EA4848',
          500: '#E31A1A',
          600: '#B71515',
          700: '#891010',
          800: '#5B0B0B',
          900: '#2E0505',
          950: '#170303',
        },
        horizonBlue: {
          50: '#EBEFFF',
          100: '#D6DFFF',
          200: '#ADBFFF',
          300: '#8AA3FF',
          400: '#6183FF',
          500: '#3965FF',
          600: '#0036FA',
          700: '#0029BD',
          800: '#001B7A',
          900: '#000D3D',
          950: '#00071F',
        },
        horizonTeal: {
          50: '#EBFAF8',
          100: '#D7F4F2',
          200: '#AAE9E4',
          300: '#82DED6',
          400: '#59D4C9',
          500: '#33C3B7',
          600: '#299E94',
          700: '#1F756E',
          800: '#144D48',
          900: '#0B2826',
          950: '#051413',
        },
        horizonPurple: {
		  50: '#EFEBFF',
          100: '#E9E3FF',
          200: '#422AFB',
          300: '#422AFB',
          400: '#7551FF',
          500: '#422AFB',
          600: '#3311DB',
          700: '#02044A',
          800: '#190793',
          900: '#11047A',
        },
		secondaryGray: {
			100: '#E0E5F2',
			200: '#E1E9F8',
			300: '#F4F7FE',
			400: '#E9EDF7',
			500: '#8F9BBA',
			600: '#A3AED0',
			700: '#707EAE',
			800: '#707EAE',
			900: '#1B2559'
		},
		red: {
			100: '#FEEFEE',
			500: '#EE5D50',
			600: '#E31A1A'
		},
		blue: {
			50: '#EFF4FB',
			500: '#3965FF'
		},
		orange: {
			100: '#FFF6DA',
			500: '#FFB547'
		},
		green: {
			100: '#E6FAF5',
			500: '#01B574'
		},
		navy: {
			50: '#d0dcfb',
			100: '#aac0fe',
			200: '#a3b9f8',
			300: '#728fea',
			400: '#3652ba',
			500: '#1b3bbb',
			600: '#24388a',
			700: '#1B254B',
			800: '#111c44',
			900: '#0b1437'
		},
		gray: {
			100: '#FAFCFE'
		},
		background: {
			100: '#FFFFFF',
			900: '#0b1437'
		},
		// Glassmorphism colors for 2026 UI trends
		glass: {
			dark: 'rgba(11, 20, 55, 0.4)',
			light: 'rgba(255, 255, 255, 0.1)',
			border: 'rgba(255, 255, 255, 0.18)',
			borderDark: 'rgba(255, 255, 255, 0.1)',
			shadow: 'rgba(0, 0, 0, 0.1)',
			shadowDark: 'rgba(0, 0, 0, 0.3)'
		},
		spatial: {
			layer1: 'rgba(66, 42, 251, 0.05)',
			layer2: 'rgba(66, 42, 251, 0.08)',
			layer3: 'rgba(66, 42, 251, 0.12)',
			glow: 'rgba(66, 42, 251, 0.15)'
		}
	},
	styles: {
		global: (props: any) => ({
			':root': {
				'--orgcloud-bg': mode('#eef3f8', modernBg)(props),
				'--orgcloud-shell-bg': mode('#f8fafc', modernShellBg)(props),
				'--orgcloud-surface': mode('rgba(255,255,255,0.96)', modernSurface)(props),
				'--orgcloud-surface-strong': mode('rgba(255,255,255,0.98)', modernSurfaceStrong)(props),
				'--orgcloud-surface-soft': mode('rgba(15,23,42,0.045)', modernSurfaceSoft)(props),
				'--orgcloud-field-bg': mode('#ffffff', 'rgba(15, 23, 42, 0.72)')(props),
				'--orgcloud-border': mode('rgba(148,163,184,0.22)', modernBorder)(props),
				'--orgcloud-border-strong': mode('rgba(148,163,184,0.34)', modernBorderStrong)(props),
				'--orgcloud-text': mode('#0f172a', modernText)(props),
				'--orgcloud-muted': mode('#475569', modernMuted)(props),
				'--orgcloud-subtle': mode('#64748b', modernSubtle)(props),
				'--orgcloud-accent': modernAccent,
				'--orgcloud-focus': mode('#4f46e5', '#a5b4fc')(props),
				'--orgcloud-error': mode('#b91c1c', '#fca5a5')(props),
				'--orgcloud-shadow': mode('0 24px 70px rgba(15, 23, 42, 0.12)', '0 24px 70px rgba(2, 6, 23, 0.32)')(props),
				'--orgcloud-radius': '28px',
				'--orgcloud-option-bg': mode('#ffffff', '#0f172a')(props),
				'--orgcloud-auth-page-bg': mode(
					'radial-gradient(circle at 18% 0%, rgba(59,130,246,0.10), transparent 32%), linear-gradient(135deg, #f8fafc 0%, #eef4fb 48%, #e6edf7 100%)',
					'radial-gradient(circle at 18% 14%, rgba(56,189,248,0.12), transparent 28%), radial-gradient(circle at 88% 12%, rgba(79,70,229,0.12), transparent 30%), linear-gradient(135deg, #07111f 0%, #091421 52%, #0b1220 100%)'
				)(props),
				'--orgcloud-auth-panel-bg': mode(
					'linear-gradient(160deg, rgba(255,255,255,0.72), rgba(226,232,240,0.58))',
					'linear-gradient(155deg, rgba(15,23,42,0.58), rgba(30,41,59,0.34))'
				)(props),
				'--orgcloud-auth-panel-border': mode('rgba(148,163,184,0.22)', 'rgba(148,163,184,0.14)')(props),
			},
			'body.chakra-ui-light [data-orgcloud-public-form="true"], [data-theme="light"] [data-orgcloud-public-form="true"]': {
				'--orgcloud-bg': '#eef3f8',
				'--orgcloud-shell-bg': '#f8fafc',
				'--orgcloud-surface': 'rgba(255,255,255,0.96)',
				'--orgcloud-surface-strong': 'rgba(255,255,255,0.98)',
				'--orgcloud-surface-soft': 'rgba(15,23,42,0.045)',
				'--orgcloud-field-bg': '#ffffff',
				'--orgcloud-option-bg': '#ffffff',
				'--orgcloud-border': 'rgba(148,163,184,0.22)',
				'--orgcloud-border-strong': 'rgba(148,163,184,0.34)',
				'--orgcloud-text': '#0f172a',
				'--orgcloud-muted': '#475569',
				'--orgcloud-subtle': '#64748b',
				'--orgcloud-focus': '#4f46e5',
				'--orgcloud-error': '#b91c1c',
				'--orgcloud-shadow': '0 24px 70px rgba(15, 23, 42, 0.12)',
				'--orgcloud-auth-page-bg': 'radial-gradient(circle at 18% 0%, rgba(59,130,246,0.10), transparent 32%), linear-gradient(135deg, #f8fafc 0%, #eef4fb 48%, #e6edf7 100%)',
				'--orgcloud-auth-panel-bg': 'linear-gradient(160deg, rgba(255,255,255,0.72), rgba(226,232,240,0.58))',
				'--orgcloud-auth-panel-border': 'rgba(148,163,184,0.22)',
			},
			'body.chakra-ui-dark [data-orgcloud-public-form="true"], [data-theme="dark"] [data-orgcloud-public-form="true"]': {
				'--orgcloud-bg': modernBg,
				'--orgcloud-shell-bg': modernShellBg,
				'--orgcloud-surface': modernSurface,
				'--orgcloud-surface-strong': modernSurfaceStrong,
				'--orgcloud-surface-soft': modernSurfaceSoft,
				'--orgcloud-field-bg': 'rgba(15, 23, 42, 0.72)',
				'--orgcloud-option-bg': '#0f172a',
				'--orgcloud-border': modernBorder,
				'--orgcloud-border-strong': modernBorderStrong,
				'--orgcloud-text': modernText,
				'--orgcloud-muted': modernMuted,
				'--orgcloud-subtle': modernSubtle,
				'--orgcloud-focus': '#a5b4fc',
				'--orgcloud-error': '#fca5a5',
				'--orgcloud-shadow': '0 24px 70px rgba(2, 6, 23, 0.32)',
				'--orgcloud-auth-page-bg': 'radial-gradient(circle at 18% 14%, rgba(56,189,248,0.12), transparent 28%), radial-gradient(circle at 88% 12%, rgba(79,70,229,0.12), transparent 30%), linear-gradient(135deg, #07111f 0%, #091421 52%, #0b1220 100%)',
				'--orgcloud-auth-panel-bg': 'linear-gradient(155deg, rgba(15,23,42,0.58), rgba(30,41,59,0.34))',
				'--orgcloud-auth-panel-border': 'rgba(148,163,184,0.14)',
			},
			'html, body, #root': {
				minHeight: '100%',
				background: mode('#eef3f8', modernBg)(props),
			},
			body: {
				overflowX: 'hidden',
				bg: mode('#eef3f8', modernBg)(props),
				fontFamily: 'DM Sans',
				letterSpacing: '-0.5px',
				color: mode('#0f172a', modernText)(props),
			},
			'body, button, input, textarea, select': {
				fontFamily: 'DM Sans',
			},
			html: {
				fontFamily: 'DM Sans'
			},
			'body.orgcloud-modern, [data-orgcloud-modern-shell="true"], [data-orgcloud-public-form="true"]': {
				background: 'var(--orgcloud-bg)',
				color: 'var(--orgcloud-text)',
			},
			'[data-orgcloud-modern-shell="true"] .chakra-card, [data-orgcloud-public-form="true"] .chakra-card': {
				background: 'var(--orgcloud-surface) !important',
				border: '1px solid var(--orgcloud-border) !important',
				boxShadow: 'var(--orgcloud-shadow) !important',
				backdropFilter: 'blur(18px)',
			},
			'[data-orgcloud-modern-shell="true"] .chakra-modal__content, [data-orgcloud-public-form="true"] .chakra-modal__content': {
				background: 'var(--orgcloud-surface-strong) !important',
				border: '1px solid var(--orgcloud-border) !important',
				borderRadius: '28px !important',
				boxShadow: '0 32px 90px rgba(2, 6, 23, 0.55) !important',
				color: 'var(--orgcloud-text) !important',
				overflow: 'hidden',
			},
			'[data-orgcloud-modern-shell="true"] .chakra-modal__header, [data-orgcloud-public-form="true"] .chakra-modal__header': {
				borderBottom: '1px solid var(--orgcloud-border)',
			},
			'[data-orgcloud-modern-shell="true"] .chakra-modal__footer, [data-orgcloud-public-form="true"] .chakra-modal__footer': {
				borderTop: '1px solid var(--orgcloud-border)',
				background: 'var(--orgcloud-surface-soft)',
			},
			'[data-orgcloud-modern-shell="true"] .chakra-input, [data-orgcloud-modern-shell="true"] .chakra-select, [data-orgcloud-modern-shell="true"] .chakra-textarea, [data-orgcloud-public-form="true"] .chakra-input, [data-orgcloud-public-form="true"] .chakra-select, [data-orgcloud-public-form="true"] .chakra-textarea': {
				background: 'var(--orgcloud-field-bg) !important',
				borderColor: 'var(--orgcloud-border-strong) !important',
				borderRadius: '18px !important',
				color: 'var(--orgcloud-text) !important',
				boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
			},
			// Explicit state rules must outrank the shared !important field baseline.
			'[data-orgcloud-modern-shell="true"] :is(.chakra-input, .chakra-select, .chakra-textarea):focus-visible, [data-orgcloud-public-form="true"] :is(.chakra-input, .chakra-select, .chakra-textarea):focus-visible': {
				borderColor: 'var(--orgcloud-focus) !important',
				outline: '2px solid var(--orgcloud-focus)',
				outlineOffset: '2px',
			},
			'[data-orgcloud-modern-shell="true"] :is(.chakra-input, .chakra-select, .chakra-textarea)[aria-invalid="true"], [data-orgcloud-public-form="true"] :is(.chakra-input, .chakra-select, .chakra-textarea)[aria-invalid="true"]': {
				borderColor: 'var(--orgcloud-error) !important',
			},
			'[data-orgcloud-modern-shell="true"] .chakra-input::placeholder, [data-orgcloud-modern-shell="true"] .chakra-textarea::placeholder, [data-orgcloud-public-form="true"] .chakra-input::placeholder, [data-orgcloud-public-form="true"] .chakra-textarea::placeholder': {
				color: 'var(--orgcloud-subtle) !important',
			},
			'[data-orgcloud-modern-shell="true"] .chakra-form__label, [data-orgcloud-public-form="true"] .chakra-form__label': {
				color: 'var(--orgcloud-muted) !important',
				fontWeight: 700,
			},
			'[data-orgcloud-modern-shell="true"] table, [data-orgcloud-public-form="true"] table': {
				borderCollapse: 'separate',
				borderSpacing: 0,
			},
			'[data-orgcloud-modern-shell="true"] thead, [data-orgcloud-public-form="true"] thead': {
				background: 'var(--orgcloud-surface-soft)',
			},
			'[data-orgcloud-modern-shell="true"] th, [data-orgcloud-public-form="true"] th': {
				color: 'var(--orgcloud-muted) !important',
				borderColor: 'var(--orgcloud-border) !important',
				letterSpacing: '0.08em',
				textTransform: 'uppercase',
			},
			'[data-orgcloud-modern-shell="true"] td, [data-orgcloud-public-form="true"] td': {
				borderColor: 'var(--orgcloud-border) !important',
				color: 'var(--orgcloud-text) !important',
			},
			'[data-orgcloud-modern-shell="true"] .chakra-badge, [data-orgcloud-public-form="true"] .chakra-badge': {
				borderRadius: '999px',
				border: '1px solid var(--orgcloud-border)',
				background: 'var(--orgcloud-surface-soft) !important',
				color: 'var(--orgcloud-muted) !important',
				letterSpacing: '0.06em',
			},
			'[data-orgcloud-modern-shell="true"] .chakra-checkbox__control, [data-orgcloud-public-form="true"] .chakra-checkbox__control': {
				borderColor: 'var(--orgcloud-border-strong) !important',
				background: 'var(--orgcloud-field-bg) !important',
			},
			'[data-orgcloud-modern-shell="true"] .chakra-checkbox__control:is([data-checked], [data-indeterminate]), [data-orgcloud-public-form="true"] .chakra-checkbox__control:is([data-checked], [data-indeterminate])': {
				borderColor: 'var(--orgcloud-accent) !important',
				background: 'var(--orgcloud-accent) !important',
				color: '#ffffff !important',
			},
			'[data-orgcloud-modern-shell="true"] .chakra-checkbox__control[data-focus-visible], [data-orgcloud-public-form="true"] .chakra-checkbox__control[data-focus-visible]': {
				outline: '2px solid var(--orgcloud-focus)',
				outlineOffset: '3px',
			},
			'[data-orgcloud-modern-shell="true"] .chakra-tabs__tab[aria-selected=true], [data-orgcloud-public-form="true"] .chakra-tabs__tab[aria-selected=true]': {
				background: 'rgba(79, 70, 229, 0.18) !important',
				color: 'var(--orgcloud-text) !important',
				borderColor: 'rgba(99, 102, 241, 0.55) !important',
			},
			'[data-orgcloud-modern-shell="true"] option, [data-orgcloud-public-form="true"] option': {
				background: 'var(--orgcloud-option-bg)',
				color: 'var(--orgcloud-text)',
			},
		})
	}
};
